/**
 * Renders one condition leaf: question + operator + value(s).
 *
 * Handles three shapes:
 *   1. Comparison       — left op right (numeric, label, or another question)
 *   2. Membership       — left op {set of labels/numbers}
 *   3. Count comparison — COUNT(question) op number  (modeled as ComparisonNode whose left.kind === "count")
 *
 * The user toggles between (1)/(3) via a "Use COUNT instead" link when the
 * selected question supports it.
 */
import React, { useMemo } from "react"
import { AutoComplete, Select, InputNumber, Button, Tooltip, Tag } from "antd"
import { DeleteOutlined, FunctionOutlined } from "@ant-design/icons"

import { LabelItem } from "utils/labelUtils"
import {
  ASTNode,
  ComparisonNode,
  ComparisonOp,
  MembershipNode,
  MembershipOp,
  Operand,
  QRefOperand,
  ARefOperand,
  NumOperand,
  CountOperand,
  COMPARISON_OPS,
  MEMBERSHIP_OPS,
  OPERATOR_LABELS,
  QuestionType,
  getAllowedOperators,
} from "utils/logicWizard/grammar"
import {
  QuestionInfo,
  resolveAnswerOptions,
} from "utils/logicWizard/questionTypeDetector"
import QuestionPicker from "./QuestionPicker.component"

interface ConditionRowProps {
  node: ASTNode
  labels: LabelItem[]
  questionInfo: Map<string, QuestionInfo>
  listMap: ReadonlyMap<string, LabelItem>
  onChange: (next: ASTNode) => void
  onDelete?: () => void
  /**
   * If provided, the row renders a "+ NOT" button alongside the trash so
   * the user can wrap this leaf in a NOT. Sits in the row's flex flow so
   * the two action buttons wrap together when the row overflows.
   */
  onWrapInNot?: () => void
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getQuestionRef(node: ASTNode): QRefOperand | null {
  if (node.kind === "cmp" || node.kind === "set-cmp") {
    if (node.left.kind === "qref") return node.left
    if (node.left.kind === "count" && node.left.arg.kind === "qref") return node.left.arg
  }
  return null
}

function isCountRow(node: ASTNode): boolean {
  return node.kind === "cmp" && node.left.kind === "count"
}

function makeEmptyComparison(label = ""): ComparisonNode {
  return {
    kind: "cmp",
    left: { kind: "qref", label } satisfies QRefOperand,
    op: "IS",
    right: { kind: "aref", label: "" } satisfies ARefOperand,
  }
}

function makeEmptyMembership(label = ""): MembershipNode {
  return {
    kind: "set-cmp",
    left: { kind: "qref", label } satisfies QRefOperand,
    op: "ANY OF",
    right: { kind: "set", items: [] },
  }
}

function makeCountRow(label = ""): ComparisonNode {
  return {
    kind: "cmp",
    left: { kind: "count", arg: { kind: "qref", label } } satisfies CountOperand,
    op: ">",
    right: { kind: "num", value: 0 } satisfies NumOperand,
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

const ConditionRow: React.FC<ConditionRowProps> = ({
  node,
  labels,
  questionInfo,
  listMap,
  onChange,
  onDelete,
  onWrapInNot,
}) => {
  // Hooks must run unconditionally — derive values for ALL node shapes,
  // then branch into the placeholder render at the bottom of the function.
  const qref = node.kind === "placeholder" ? null : getQuestionRef(node)
  const qInfo = qref ? questionInfo.get(qref.label) : undefined
  const isOrdinal = qInfo?.isOrdinal ?? false
  const ops = qInfo
    ? getAllowedOperators(qInfo.type, isOrdinal)
    : getAllowedOperators(QuestionType.UNKNOWN)

  const answerOptions = useMemo(
    () => (qInfo ? resolveAnswerOptions(qInfo, listMap) : []),
    [qInfo, listMap],
  )

  const operatorOptions = useMemo(() => {
    const out: { value: string; label: string; group?: string }[] = []
    for (const op of ops.comparison) {
      out.push({ value: op, label: OPERATOR_LABELS[op], group: "Compare" })
    }
    for (const op of ops.membership) {
      out.push({ value: op, label: OPERATOR_LABELS[op], group: "Set" })
    }
    if (ops.countSupported && !isCountRow(node)) {
      out.push({
        value: "__COUNT__",
        label: "Use COUNT instead…",
        group: "Function",
      })
    }
    if (isCountRow(node)) {
      out.push({
        value: "__SWITCH_TO_VALUE__",
        label: "Compare values instead…",
        group: "Function",
      })
    }
    return out
  }, [ops, node])

  // Initial render of an empty placeholder: stamp out a starter Comparison.
  if (node.kind === "placeholder") {
    return (
      <div className="flex items-center gap-2 py-1">
        <QuestionPicker
          labels={labels}
          questionInfo={questionInfo}
          value=""
          onChange={(label) => onChange(makeEmptyComparison(label))}
        />
        <span className="text-xs text-gray-400">
          Pick a question to begin
        </span>
        {onDelete && (
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={onDelete}
            danger
          />
        )}
      </div>
    )
  }

  const handleQuestionChange = (label: string) => {
    if (node.kind === "cmp" && node.left.kind === "count") {
      onChange({
        ...node,
        left: { kind: "count", arg: { kind: "qref", label } },
      } satisfies ComparisonNode)
      return
    }
    if (node.kind === "cmp") {
      onChange({ ...node, left: { kind: "qref", label } })
      return
    }
    if (node.kind === "set-cmp") {
      onChange({ ...node, left: { kind: "qref", label } })
      return
    }
  }

  const handleOpChange = (newOp: string) => {
    if (newOp === "__COUNT__") {
      onChange(makeCountRow(qref?.label ?? ""))
      return
    }
    if (newOp === "__SWITCH_TO_VALUE__") {
      onChange(makeEmptyComparison(qref?.label ?? ""))
      return
    }
    if (COMPARISON_OPS.includes(newOp as ComparisonOp)) {
      if (node.kind === "set-cmp") {
        onChange(makeEmptyComparison(qref?.label ?? ""))
        return
      }
      if (node.kind === "cmp") {
        // For COUNT rows, force numeric right operand if not already.
        const right: Operand =
          isCountRow(node) && node.right.kind !== "num"
            ? { kind: "num", value: 0 }
            : node.right
        onChange({ ...node, op: newOp as ComparisonOp, right })
        return
      }
    }
    if (MEMBERSHIP_OPS.includes(newOp as MembershipOp)) {
      if (node.kind === "cmp") {
        onChange({
          kind: "set-cmp",
          left: { kind: "qref", label: qref?.label ?? "" },
          op: newOp as MembershipOp,
          right: { kind: "set", items: [] },
        })
        return
      }
      if (node.kind === "set-cmp") {
        onChange({ ...node, op: newOp as MembershipOp })
        return
      }
    }
  }

  const currentOp = node.kind === "cmp" || node.kind === "set-cmp" ? node.op : ""

  return (
    <div className="flex items-center gap-2 py-1 flex-wrap">
      {/* Question / COUNT prefix */}
      {isCountRow(node) && (
        <Tag color="purple" icon={<FunctionOutlined />}>
          COUNT
        </Tag>
      )}
      <QuestionPicker
        labels={labels}
        questionInfo={questionInfo}
        value={qref?.label ?? ""}
        onChange={handleQuestionChange}
      />

      {/* Operator */}
      <Select
        value={currentOp}
        onChange={handleOpChange}
        style={{ minWidth: 180 }}
        placeholder="Operator..."
        disabled={!qInfo}
        options={operatorOptions.map((o) => ({
          value: o.value,
          label: o.label,
        }))}
      />

      {/* Value side */}
      <ValueEditor
        node={node}
        qInfo={qInfo}
        answerOptions={answerOptions}
        onChange={onChange}
      />

      {onDelete && (
        <Tooltip title="Remove this condition">
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={onDelete}
            danger
          />
        </Tooltip>
      )}

      {onWrapInNot && (
        <Tooltip title="Negate. Fires when this condition is FALSE">
          <Button
            size="small"
            type="default"
            onClick={onWrapInNot}
            style={{ flexShrink: 0 }}
          >
            + NOT
          </Button>
        </Tooltip>
      )}

      {qInfo?.type === QuestionType.FILL_IN_TEXT && (
        <span className="text-xs text-orange-500 ml-2">
          Note: comparisons on FILL IN TEXT are not yet supported by the engine.
        </span>
      )}
    </div>
  )
}

// ─── Value editor (right-hand side) ──────────────────────────────────────────

interface ValueEditorProps {
  node: ASTNode
  qInfo?: QuestionInfo
  answerOptions: string[]
  onChange: (next: ASTNode) => void
}

const ValueEditor: React.FC<ValueEditorProps> = ({
  node,
  qInfo,
  answerOptions,
  onChange,
}) => {
  if (node.kind === "cmp") {
    const isCount = node.left.kind === "count"
    const isNumericQuestion =
      qInfo?.type === QuestionType.FILL_IN_NUMBER ||
      qInfo?.type === QuestionType.FILL_IN_REAL ||
      isCount

    if (isNumericQuestion) {
      const value = node.right.kind === "num" ? node.right.value : 0
      return (
        <InputNumber
          value={value}
          onChange={(v) =>
            onChange({
              ...node,
              right: { kind: "num", value: v ?? 0 },
            } satisfies ComparisonNode)
          }
          style={{ minWidth: 120 }}
        />
      )
    }

    // Label/string value — single-value combobox of inline options with
    // free-text fallback. AutoComplete is the right primitive here:
    // `Select mode="tags"` expects an array value and won't render a single
    // string correctly when re-hydrating an existing condition.
    const currentLabel =
      node.right.kind === "aref" || node.right.kind === "qref"
        ? node.right.label
        : node.right.kind === "num"
          ? String(node.right.value)
          : ""

    return (
      <AutoComplete
        value={currentLabel}
        onChange={(v: string) =>
          onChange({
            ...node,
            right: { kind: "aref", label: v ?? "" },
          } satisfies ComparisonNode)
        }
        placeholder="Pick or type an answer..."
        style={{ minWidth: 240 }}
        allowClear
        options={answerOptions.map((o) => ({ value: o }))}
        filterOption={(input, option) => {
          const v = typeof option?.value === "string" ? option.value : ""
          return v.toLowerCase().includes(input.toLowerCase())
        }}
      />
    )
  }

  if (node.kind === "set-cmp") {
    const values = node.right.items
      .map((item) =>
        item.kind === "aref" || item.kind === "qref"
          ? item.label
          : item.kind === "num"
            ? String(item.value)
            : "",
      )
      .filter(Boolean)
    return (
      <Select
        mode="tags"
        value={values}
        placeholder="Pick or type one or more answers..."
        style={{ minWidth: 320 }}
        onChange={(arr: string[]) => {
          const items: Operand[] = arr.map((s) => {
            const num = Number(s)
            if (s !== "" && !Number.isNaN(num)) {
              return { kind: "num", value: num } satisfies NumOperand
            }
            return { kind: "aref", label: s } satisfies ARefOperand
          })
          onChange({ ...node, right: { kind: "set", items } } satisfies MembershipNode)
        }}
        options={answerOptions.map((o) => ({ value: o, label: o }))}
      />
    )
  }

  return null
}

export default ConditionRow
export { makeEmptyComparison, makeEmptyMembership, makeCountRow }
